import type { AppContext } from '@application/context/AppContext';
import { NetworkStatusService } from '@infra/network/NetworkStatusService';
import { logger } from '@infra/telemetry/Logger';
import { hasHybridConsent } from './HybridConsent';
import { HybridVaultService } from './HybridVaultService';
import { HybridLiveSyncService } from './HybridLiveSyncService';
import { msUntilNextNightly } from './HybridVaultPolicy';
import { onActiveTenantChanged, resolveActiveTenantId } from '@application/licence/ActiveTenant';

let started = false;
let stopSwitchListener: (() => void) | null = null;

/**
 * Planificateur Hybride. Le tenant est relu à CHAQUE déclenchement (jamais capturé) : après
 * une bascule d'entreprise (cabinet), le tick suivant — et un tick immédiat — vise la nouvelle
 * entreprise active, avec son propre accord Hybride et son propre certificat.
 */
export function startHybridVaultScheduler(ctx: AppContext, _tenantId?: string): void {
  if (started) return;
  started = true;

  const tick = (): void => {
    const tenantId = resolveActiveTenantId(ctx);
    if (!tenantId || !HybridVaultService.isEnabled(ctx)) return;
    if (!hasHybridConsent(tenantId)) return;
    void HybridVaultService.sendIfDue(ctx, tenantId);
  };
  const syncActive = (): void => {
    const tenantId = resolveActiveTenantId(ctx);
    if (!tenantId || !NetworkStatusService.isBrowserOnline()) return;
    void HybridLiveSyncService.syncOnReconnect(ctx, tenantId);
  };

  tick();
  syncActive();
  window.setTimeout(() => {
    tick();
    window.setInterval(tick, 60 * 60 * 1000);
  }, msUntilNextNightly());

  NetworkStatusService.subscribe((online) => {
    if (online) {
      syncActive();
      tick();
    }
  });

  // Bascule d'entreprise : l'entreprise ouverte est sauvegardée / synchronisée sans attendre.
  stopSwitchListener = onActiveTenantChanged(() => {
    tick();
    syncActive();
  });

  logger.info({ event: 'hybrid_vault_scheduler_on' });
}

/** Tests : remet le planificateur à zéro. */
export function __resetHybridVaultSchedulerForTests(): void {
  started = false;
  stopSwitchListener?.();
  stopSwitchListener = null;
}
